import { AdPlatform, StorePlatform } from './enums.js';
import { DEMO_REF_PREFIX } from './demo.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * SOHBETTEN BAĞLANTI — `connect_platform` aracının sözleşmesi (30 Eyl 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Ürün sahibi: "İnsanlar platform bağlamayı bile sohbetten halletsin — sohbette
 * 'Bağlan'a tıklasın, paneldeki gibi bağlansın." Model araç çağırıyor, sohbette
 * bir BAĞLANTI KARTI çiziliyor (`AgentCard` → `kind: 'connect'`); düğme
 * bağlantılar sayfasının kullandığı uçların AYNISINI çağırıyor ve OAuth'un
 * dönüşü sohbetin kendi adresi.
 *
 * Bu dosya iki tarafın ORTAK kısmı:
 *
 *   · hangi platform sohbetten bağlanabilir (reklam + mağaza),
 *   · bağlantının DURUMU nasıl türetilir — sunucu (araç sonucu, dönüşte kartın
 *     tazelenmesi) ve arayüz (dönüşte kartın kendi okuması) AYNI fonksiyonu
 *     kullanıyor; iki kopya yazılsaydı kart "bağlı" derken model "bağlı
 *     değil" okurdu,
 *   · platformun resmî kayıt adresi — model adres UYDURMASIN, kart da aynı
 *     adresi "hesabın yok mu?" satırında versin.
 *
 * 🔴 SIR YOK. Kart ve araç yalnız platform + durum taşır. Mağaza anahtarları
 * kartın formundan DOĞRUDAN `POST /commerce/connect/credentials`e gider;
 * modele, sohbet kaydına, araç girdisine/sonucuna hiçbir zaman girmez.
 */

/**
 * Sohbetten bağlanabilen REKLAM platformları. `OPENAI` yok (adapter yok,
 * bağlantılar sayfası da listelemiyor).
 */
export const AGENT_CONNECT_AD_PLATFORMS = [
  AdPlatform.META,
  AdPlatform.GOOGLE,
  AdPlatform.TIKTOK,
  AdPlatform.LINKEDIN,
  AdPlatform.TWITTER,
] as const;
export type AgentConnectAdPlatform = (typeof AGENT_CONNECT_AD_PLATFORMS)[number];

/** Mağaza platformlarının TAMAMI — commerce modülünün desteklediği liste. */
export const AGENT_CONNECT_STORE_PLATFORMS = Object.values(StorePlatform) as StorePlatform[];

export type AgentConnectTarget = 'ad' | 'store';

/**
 * Kartın durumu.
 *
 *   unavailable     platform şu an bağlanamıyor (adapter yok / sunucu anahtarı eksik)
 *   not_connected   hiç bağlantı yok → "Bağlan"
 *   choose_account  (reklam) izin verilmiş, hesap seçilmemiş → kartın İÇİNDE seçim
 *   connected       bağlı — hesap/mağaza adıyla. Platform başına TEK hesap:
 *                   başka bir hesap isteniyorsa önce bu kaldırılır ("Hesabı değiştir")
 *   reconnect       bağlantı erişimini kaybetti → reklamda "Yeniden bağlan"
 *                   (hesaplar ve geçmiş korunur), mağazada kaldırıp yeniden bağlama
 */
export const AGENT_CONNECT_STATES = [
  'unavailable',
  'not_connected',
  'choose_account',
  'connected',
  'reconnect',
] as const;
export type AgentConnectState = (typeof AGENT_CONNECT_STATES)[number];

export interface AgentConnectStatus {
  state: AgentConnectState;
  /// Bağlı reklam hesabının / mağazanın adı — platformdan gelen, kullanıcının yazdığı metin.
  account: string | null;
  /// Reklam bağlantısının kimliği (yeniden bağlama ve hesap seçimi bununla).
  connectionId: string | null;
  /// Yer DEMO hesapla dolu (`DEMO_REF_PREFIX`) — gerçek hesap için önce demo veri kalkmalı.
  demo: boolean;
}

/**
 * Platformun RESMÎ reklam yöneticisi / kayıt adresi — hesabı hiç olmayan
 * kullanıcıya verilen adres. Model bu tablodan okuyor, uydurmuyor.
 */
export const AGENT_CONNECT_SIGNUP_URL: Record<AgentConnectAdPlatform, string> = {
  META: 'https://business.facebook.com/',
  GOOGLE: 'https://ads.google.com/',
  TIKTOK: 'https://ads.tiktok.com/',
  LINKEDIN: 'https://www.linkedin.com/campaignmanager/',
  TWITTER: 'https://ads.x.com/',
};

/** Platform kimliği → hedef (reklam mı mağaza mı). Tanınmayan → `null`. */
export function agentConnectTargetOf(platform: string): AgentConnectTarget | null {
  if ((AGENT_CONNECT_AD_PLATFORMS as readonly string[]).includes(platform)) return 'ad';
  if ((AGENT_CONNECT_STORE_PLATFORMS as readonly string[]).includes(platform)) return 'store';
  return null;
}

/**
 * Modelin yazdığı platform adını enum değerine çevirir — "x", "Facebook",
 * "instagram", "YouTube", "woo" gibi günlük adlar. Tanınmayan değer olduğu
 * gibi (büyük harfle) döner ve şema onu reddeder.
 */
export function normalizeAgentConnectPlatform(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  const key = value.trim().toUpperCase().replace(/[\s_.-]+/g, '');
  return CONNECT_ALIASES[key] ?? key;
}

const CONNECT_ALIASES: Record<string, string> = {
  X: AdPlatform.TWITTER,
  TWITTERX: AdPlatform.TWITTER,
  FACEBOOK: AdPlatform.META,
  INSTAGRAM: AdPlatform.META,
  FB: AdPlatform.META,
  GOOGLEADS: AdPlatform.GOOGLE,
  ADWORDS: AdPlatform.GOOGLE,
  YOUTUBE: AdPlatform.GOOGLE,
  TIKTOKADS: AdPlatform.TIKTOK,
  LINKEDINADS: AdPlatform.LINKEDIN,
  WOO: StorePlatform.WOOCOMMERCE,
  TSOFTCOM: StorePlatform.TSOFT,
};

// ═══════════════════════════════════════════════════════════════════════════
// Durumun türetilmesi — SAF, iki taraf da çağırıyor
// ═══════════════════════════════════════════════════════════════════════════

/** `ConnectionRow`un gereken kısmı. */
export interface AgentConnectAdConnection {
  id: string;
  platform: string;
  status: string;
}

/** `AdAccountRow`un gereken kısmı. */
export interface AgentConnectAdAccount {
  connectionId: string;
  platform: string;
  name: string;
  platformRef: string;
}

/** `StoreConnectionDto`nun gereken kısmı. */
export interface AgentConnectStore {
  platform: string;
  status: string;
  displayName: string | null;
  domain: string | null;
  externalId: string;
}

/**
 * Reklam platformunun durumu.
 *
 * Sayım HESAP ekseninde — `checkPlatformSlot` ile aynı: yeri tutan şey bağlı
 * hesap satırı, OAuth bağlantısı değil. Demo hesap da yer tutuyor (sunucu onu
 * ayırmıyor), o yüzden "bağlı" sayılıyor ama `demo` işaretiyle.
 */
export function deriveAdConnectStatus(
  platform: string,
  connections: readonly AgentConnectAdConnection[],
  accounts: readonly AgentConnectAdAccount[],
): AgentConnectStatus {
  const account = accounts.find((row) => row.platform === platform) ?? null;
  if (account) {
    const connection = connections.find((row) => row.id === account.connectionId) ?? null;
    return {
      state: connection?.status === 'DISCONNECTED' ? 'reconnect' : 'connected',
      account: account.name,
      connectionId: account.connectionId,
      demo: account.platformRef.startsWith(DEMO_REF_PREFIX),
    };
  }

  const own = connections.filter((row) => row.platform === platform);
  const live = own.find((row) => row.status !== 'DISCONNECTED') ?? null;
  if (live) return { state: 'choose_account', account: null, connectionId: live.id, demo: false };
  const dead = own[0] ?? null;
  if (dead) return { state: 'reconnect', account: null, connectionId: dead.id, demo: false };
  return { state: 'not_connected', account: null, connectionId: null, demo: false };
}

/**
 * Mağaza platformunun durumu. Kopmuş (`DISCONNECTED`) mağaza da yeri tutuyor
 * (`occupiedStoreSlot`) — panelde yeniden bağlama akışı yok; kaldırılıp
 * yeniden bağlanıyor, kart bunu söylüyor.
 */
export function deriveStoreConnectStatus(
  platform: string,
  stores: readonly AgentConnectStore[],
): AgentConnectStatus {
  const store = stores.find((row) => row.platform === platform) ?? null;
  if (!store) return { state: 'not_connected', account: null, connectionId: null, demo: false };
  return {
    state: store.status === 'DISCONNECTED' ? 'reconnect' : 'connected',
    account: store.displayName ?? store.domain ?? store.externalId,
    connectionId: null,
    demo: false,
  };
}
